import * as React from 'react';
import { Send, Paperclip, X, Lock, Globe, ChevronDown, Check } from 'lucide-react';
import { Button, MultiSelect, Tabs, useToast } from '@/components/ui';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api-client';
import { attachmentSizeError, partitionAttachmentFiles } from '@/lib/attachments';
import { useAttachmentUploader } from '@/lib/attachment-uploader';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';

type ConversationType = 'reply' | 'note';

interface MentionableAgent {
  id: string;
  name: string;
  email: string;
}

export interface ReplyConversation {
  id: string;
  type: string;
  body: string;
  isPrivate: boolean;
  createdAt: string;
  editedAt?: string | null;
  user: { id: string; firstName: string; lastName: string; email: string; avatar: string | null } | null;
  attachments: { id: string; fileName: string; fileSize: number; contentType: string }[];
  thread?: { id: string; _count?: { messages: number } } | null;
}

export interface ReplySentEvent {
  conversation: ReplyConversation;
  attachmentsPending: boolean;
  slaState?: unknown;
}

/**
 * NOTIF-01 (mention): pulls the structured mention target IDs the editor
 * stamped onto each inserted mention span (data-mention-user-id), so the
 * composer can submit them alongside the body instead of relying on the
 * backend to re-derive "who was mentioned" from visible @name/@email text.
 */
export function extractMentionedUserIds(html: string): string[] {
  if (!html || typeof document === 'undefined') return [];
  const container = document.createElement('div');
  container.innerHTML = html;
  const ids = Array.from(container.querySelectorAll('[data-mention-user-id]'))
    .map((el) => el.getAttribute('data-mention-user-id'))
    .filter((id): id is string => !!id);
  return Array.from(new Set(ids));
}

interface ReplyComposerProps {
  ticketId: string;
  onSent?: (event: ReplySentEvent) => void;
}

/**
 * Reply / Note composer. Forward is deliberately NOT a tab here — per the
 * reference UI, forwarding is triggered per-conversation (icon on a specific
 * reply/note card) and opens its own side drawer with that conversation's
 * content pre-filled, rather than living in this shared composer.
 *
 * Which tabs are visible (Reply, Note) is fully RBAC-driven via `can()` —
 * `availableTabs` never hardcodes a role. The static defaults live in
 * ticket-permissions.ts (agent defaults to Note only; super_admin can grant
 * Reply/Forward to any role from Settings > Roles at any time).
 */
export function ReplyComposer({ ticketId, onSent }: ReplyComposerProps) {
  const { can, user } = useAuth();
  const { enqueue } = useAttachmentUploader();
  const { toast } = useToast();
  const { t } = useI18n();
  const availableTabs = React.useMemo((): { key: ConversationType; label: string }[] => (
    [
      { key: 'reply' as const, label: t('tp.reply.tabReply') },
      { key: 'note' as const, label: t('tp.reply.tabNote') },
    ].filter((tab) => can(tab.key))
  ), [can, t]);
  const [type, setType] = React.useState<ConversationType>('reply');
  const [body, setBody] = React.useState('');
  const [expanded, setExpanded] = React.useState(false);
  const [notePrivate, setNotePrivate] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [files, setFiles] = React.useState<File[]>([]);
  const [notifyOptions, setNotifyOptions] = React.useState<MentionableAgent[]>([]);
  const [notifyUserIds, setNotifyUserIds] = React.useState<string[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const editorKey = React.useRef(0);

  // Always load the current account-scoped agent list when the note composer
  // opens; newly created/deactivated agents must not remain in a stale cache.
  React.useEffect(() => {
    if (type !== 'note' || !user?.id) return;

    let active = true;
    api
      .get<{ data: MentionableAgent[] }>('/tickets/mentionable-agents')
      .then((res) => {
        if (!active) return;
        const options = Array.isArray(res) ? res : res.data ?? [];
        setNotifyOptions(options);
      })
      .catch(() => {
        if (active) setNotifyOptions([]);
      });
    return () => { active = false; };
  }, [type, user?.id]);

  React.useEffect(() => {
    if (body.trim()) setExpanded(true);
  }, [body]);

  React.useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.some((tab) => tab.key === type)) {
      setType(availableTabs[0].key);
    }
  }, [availableTabs, type]);

  const hasDraft = body.trim().length > 0 || files.length > 0 || notifyUserIds.length > 0;

  function handleCancel() {
    setBody('');
    setError(null);
    setFiles([]);
    setNotifyUserIds([]);
    setExpanded(false);
    editorKey.current += 1;
  }

  async function handleSubmit() {
    if (!body.trim()) return;
    setError(null);
    setSending(true);

    try {
      const payload: Record<string, unknown> = { type, body };
      if (type === 'note') {
        payload.isPrivate = notePrivate;
        if (notifyUserIds.length > 0) payload.notifyUserIds = notifyUserIds;
      }
      const mentionedUserIds = extractMentionedUserIds(body);
      if (mentionedUserIds.length > 0) payload.mentionedUserIds = mentionedUserIds;

      const convRes = await api.post<{ data: ReplyConversation & { slaState?: unknown } }>(
        `/tickets/${ticketId}/conversations`,
        payload,
      );
      const attachmentsPending = files.length > 0;

      // The backend already queues the canonical ticket_reply notification,
      // which renders the configured template and sends to all stakeholders.
      // Upload attachments independently; do not call the legacy direct-send
      // endpoint here or the reply is delivered a second time as raw HTML.
      if (attachmentsPending) {
        enqueue(ticketId, files, {
          conversationId: convRes.data.id,
        });
      }

      toast({
        variant: 'success',
        title: type === 'note' ? t('tp.reply.noteAdded') : t('tp.reply.replySent'),
      });

      setBody('');
      setExpanded(false);
      setFiles([]);
      setNotifyUserIds([]);
      editorKey.current += 1;
      onSent?.({
        conversation: convRes.data,
        attachmentsPending,
        slaState: convRes.data.slaState,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('tp.reply.failedToSend');
      setError(message);
      toast({ variant: 'error', title: t('tp.reply.failedToSend'), description: message });
    } finally {
      setSending(false);
    }
  }

  if (availableTabs.length === 0) return null;
  if (!expanded) {
    return (
      <button type="button" onClick={() => setExpanded(true)} className="flex w-full items-center justify-between rounded-lg border border-dashed border-line-strong bg-surface px-4 py-3 text-left text-sm text-ink-subtle hover:border-brand-400 hover:text-brand-700">
        <span>{type === 'note' ? t('tp.reply.collapsedNote') : t('tp.reply.collapsedReply')}</span>
        <span className="text-xs">{t('tp.reply.clickToExpand')}</span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg border',
        type === 'note' ? 'border-warning-500/30 bg-warning-50' : 'border-line',
      )}
    >
      <div className="flex items-center gap-2 border-b px-4">
        <Tabs
          aria-label={t('tp.reply.conversationType')}
          className="min-w-0 flex-1 border-b-0 px-0"
          items={availableTabs.map((tab) => ({ value: tab.key, label: tab.label }))}
          value={type}
          onChange={(value) => setType(value as ConversationType)}
        />
        {type === 'note' && (
          <DropdownMenu>
            <DropdownMenuTrigger className="flex shrink-0 items-center gap-1.5 rounded-md border border-warning-500/40 bg-surface px-2.5 py-1 text-xs font-medium text-warning-700 hover:bg-warning-50">
              {notePrivate ? <Lock className="h-3 w-3" aria-hidden="true" /> : <Globe className="h-3 w-3" aria-hidden="true" />}
              <span className="sm:hidden">{notePrivate ? t('tp.reply.private') : t('tp.reply.public')}</span>
              <span className="hidden sm:inline">{notePrivate ? t('tp.reply.notVisibleToContact') : t('tp.reply.visibleToContact')}</span>
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <p className="px-2 py-1 text-[11px] font-semibold uppercase text-ink-subtle">{t('tp.reply.markNoteAs')}</p>
              <DropdownMenuItem onClick={() => setNotePrivate(false)} className="items-start gap-2">
                <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" aria-hidden="true" />
                <span className="flex-1">
                  <span className="block font-medium text-ink">{t('tp.reply.public')}</span>
                  <span className="block text-xs text-ink-subtle">{t('tp.reply.visibleToContacts')}</span>
                </span>
                {!notePrivate && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden="true" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setNotePrivate(true)} className="items-start gap-2">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" aria-hidden="true" />
                <span className="flex-1">
                  <span className="block font-medium text-ink">{t('tp.reply.private')}</span>
                  <span className="block text-xs text-ink-subtle">{t('tp.reply.notVisibleToContacts')}</span>
                </span>
                {notePrivate && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden="true" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {type === 'note' && (
        <div className="border-b px-4 py-2">
          <MultiSelect
            label={t('tp.reply.notifyTo')}
            placeholder={t('tp.reply.searchAgentsToNotify')}
            options={notifyOptions.map((agent) => ({ value: agent.id, label: agent.name }))}
            value={notifyUserIds}
            onChange={setNotifyUserIds}
            searchable
          />
        </div>
      )}

      <div className="p-4">
        <RichTextEditor
          key={editorKey.current}
          value=""
          onChange={setBody}
          placeholder={
            type === 'note'
              ? notePrivate ? t('tp.reply.placeholderPrivateNote') : t('tp.reply.placeholderPublicNote')
              : t('tp.reply.placeholderReply')
          }
          minHeight="100px"
          onAttach={() => fileInputRef.current?.click()}
          mentionsEnabled
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => {
            const selected = e.target.files ? Array.from(e.target.files) : [];
            e.target.value = '';
            const { accepted, rejected } = partitionAttachmentFiles(selected);
            if (rejected.length > 0) {
              const message = attachmentSizeError(rejected);
              setError(message);
              toast({ variant: 'error', title: t('tp.reply.attachmentNotAdded'), description: message });
            }
            if (accepted.length > 0) setFiles((current) => [...current, ...accepted]);
          }}
          className="hidden"
          aria-label={t('tp.reply.uploadAttachment')}
        />
      </div>

      {files.length > 0 && (
        <div className="space-y-1 px-4 pb-2">
          {files.map((file, i) => (
            <div key={i} className="flex items-center justify-between rounded bg-surface-hover px-3 py-1.5 text-xs">
              <span className="truncate">
                <Paperclip className="mr-1 inline h-3 w-3" aria-hidden="true" />
                {file.name} ({(file.size / 1024).toFixed(0)} KB)
              </span>
              <button
                type="button"
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
                className="text-ink-subtle hover:text-danger-600"
                aria-label={t('tp.reply.removeFile', { name: file.name })}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 px-4 pb-4">
        <div className="flex items-center gap-2">
          {error && (
            <span className="text-xs text-danger-600" role="alert">
              {error}
            </span>
          )}
          {hasDraft && (
            <Button variant="ghost" size="sm" onClick={handleCancel} disabled={sending}>
              {t('tp.common.cancel')}
            </Button>
          )}
          <Button size="sm" onClick={handleSubmit} disabled={sending || !body.trim()}>
            <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {sending ? t('tp.reply.sending') : type === 'note' ? t('tp.reply.addNote') : t('tp.reply.sendReply')}
          </Button>
        </div>
      </div>
    </div>
  );
}
